<template>
    <v-row
        no-gutters
        class="pr-8 pl-6 pt-8"
    >
        <v-col cols="12">
            <AppStoreListHeader
                :item-label="$t('Email signatures')"
                :add-label="$t('Add')"
                show-button
                @add-item="onEditSignature(null)"
            />
            <v-row
                v-if="emailSignatures.length > 0"
                no-gutters
            >
                <v-col cols="12">
                    <data-table
                        :items="emailSignatures"
                        :headers="headers"
                        row-clickable
                        @click:row="onEditSignature($event.id)"
                    >
                        <template v-slot:item.title="{ item }">
                            <span class="body-2">{{ item.title }}</span>
                            <v-icon v-if="item.is_default" class="ml-3" color="secondary" :size="20">
                                mdi-star
                            </v-icon>
                        </template>
                        <template v-slot:item.is_for_everyone="{ item }">
                            {{ item.is_for_everyone ? $t('Everyone') : $t('Only me') }}
                        </template>
                        <template v-slot:item.person="{ item }">
                            {{ getPersonFullname(item) }}
                        </template>
                    </data-table>
                </v-col>
            </v-row>
        </v-col>
        <EditEmailSignatureDialog
            v-if="signature"
            :show-dialog="Boolean(signature)"
            :signature="signature"
            :errors="errors"
            @close="closeDialog"
            @save="onSaveSignature"
            @delete="onDeleteSignature"
            @sendTestEmail="sendTestEmailOut"
        />
    </v-row>
</template>

<script>

import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader.vue";
import {computed, onMounted, ref} from "@vue/composition-api";
import EditEmailSignatureDialog from "../../Dialog/EditEmailSignatureDialog/Index.vue";
import {$t} from "@/i18n";
import emailSignature from "../../../api/email_automation/email_signature";
import appStoreDialog from "../../../store/modules/AppStoreDialog/appStoreDialog";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import DialogModule from "../../../store/modules/dialog";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "EmailingSignaturesTab",
    components: {EditEmailSignatureDialog, AppStoreListHeader},
    setup() {
        const errors = ref({})
        const signature = ref(null)
        const {
            saveEmailSignature,
            fetchEmailSignatures,
            fetchEmailSignature,
            emailSignaturesList,
            deleteSignature,
            sendTestEmail,
        } = emailSignature()
        const {
            setConfirmOptions,
            openConfirmDialog,
        } = DialogModule();
        const {appStoreDialogGetters} = appStoreDialog()
        const {showFeedbackNotification} = FeedbackNotificationModule();

        const currentCompany = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const emailSignatures = computed(function () {
            return emailSignaturesList.value;
        });

        const headers = ref([
            {
                text: $t("Name"),
                value: "title",
                sortable: false,
                width: '60%'
            },
            {
                text: $t("Access"),
                value: "is_for_everyone",
                sortable: false
            },
            {
                text: $t("Creator"),
                value: "person",
                sortable: false
            },
            {
                value: "edit"
            }
        ])

        const onEditSignature = async (id) => {
            if (id) {
                signature.value = await fetchEmailSignature(id, currentCompany.value.id)
            } else {
                signature.value = {
                    id: null,
                    company_id: currentCompany.value.id,

                    title: "",
                    layout: "logo_left",

                    is_for_everyone: true,
                    is_default: false,

                    content_top: "",
                    content_middle: "",
                    content_bottom: "",

                    linkedin_link: null,
                    facebook_link: null,
                    other_link: null,
                    logo_url: null,
                }
            }
        }

        const closeDialog = () => {
            signature.value = null
            errors.value = {}
        }

        const onSaveSignature = async (signature) => {
            try {
                await saveEmailSignature(signature)
                await fetchEmailSignatures(currentCompany.value.id)

                closeDialog()
            } catch (error) {
                if (error.response?.data?.error_type === "access") {
                    errorDialog.showError(error)
                } else {
                    errors.value = error
                }
            }
        }

        const onDeleteSignature = () => {
            const title = $t("Are you sure you want to delete this signature? Some emails will be affected")

            setConfirmOptions({
                title: title,
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => deleteCurrentSignature()
            });
            openConfirmDialog();
        }

        const deleteCurrentSignature = async () => {
            try {
                const signatureId = signature.value.id
                const companyId = signature.value.company_id
                await deleteSignature(signatureId, companyId)
                closeDialog()
            } catch (error) {
                errorDialog.showError(error)
            }

        }

        const getPersonFullname = (item) => {
            return item.person ? `${item.person.name} ${item.person.surname}` : ''
        }

        const sendTestEmailOut = async (signature) => {
            try {
                await sendTestEmail(signature)
                showFeedbackNotification($t('Test email sent'))
            } catch (error) {
                if (error.response?.data) {
                    errors.value = error
                }
            }
        }

        onMounted(async () => {
            await fetchEmailSignatures(currentCompany.value.id)
        })

        return {
            onEditSignature,
            closeDialog,
            onSaveSignature,
            headers,
            emailSignatures,
            signature,
            errors,
            getPersonFullname,
            onDeleteSignature,
            sendTestEmailOut,
        }
    }
}
</script>

<style scoped lang="scss">

</style>